import { css, useTheme } from '@emotion/react'
import styled from '@emotion/styled'

import { spacing } from '@cash-design-system/react'
import {
  FormBlockerElementRemoteImageElement,
  FormBlockerElementRemoteImageElementHorizontalAlignment,
} from '@cash-web/protos/squareup/franklin/form_blocker.pb'
import { useArcadeMigrationFeatureFlag } from '@cash-web/shared-data-access-arcade-migration'
import { replaceWithCDNImageUrl } from '@cash-web/shared-util-url'

const Alignment = styled.div<{
  alignment?: FormBlockerElementRemoteImageElementHorizontalAlignment
}>`
  width: 100%;
  display: flex;

  ${props => {
    switch (props.alignment) {
      case FormBlockerElementRemoteImageElementHorizontalAlignment.START:
        return css`
          justify-content: left;
        `
      case FormBlockerElementRemoteImageElementHorizontalAlignment.CENTER:
        return css`
          justify-content: center;
        `
      case FormBlockerElementRemoteImageElementHorizontalAlignment.END:
        return css`
          justify-content: flex-end;
        `
      default:
        return ''
    }
  }}
`

const RemoteImageContainer = styled.div<{
  name?: string
  dimensions?: { width?: number; height?: number }
  isArcadeMigrationEnabled?: boolean
}>`
  /* handle custom styling for images that need to be tweaked for web. */
  ${props => {
    switch (props.name) {
      case 'staticmap':
        return css`
          margin: ${spacing.l} ${props.isArcadeMigrationEnabled ? 0 : spacing.xs};
          border-radius: 0.75rem;
          overflow: hidden;
          display: flex;
          background: ${props.theme.arcadeColors.semantic.backgroundProminent};
          width: 16rem;
        `
      case 'dda_form_share_blocker_image':
        return css`
          width: 100%;

          img {
            ${props.dimensions?.width ? `width: ${props.theme.pxToRem(props.dimensions?.width)}` : ''};
          }
        `
      case 'round-ups-intro-nux-burger-light':
      case 'round-ups-intro-nux-burger-dark':
        // For some reason, this image totally overflows the container, so we constrain it to just 100% width.
        // Ideally at some point we can figure out how to get this to work by default. See CASHW-3768.
        return css`
          width: 100%;
          margin: ${spacing.empty} ${props.isArcadeMigrationEnabled ? 0 : spacing.xs};
        `
      case 'Goals-v2@3x':
        return css`
          ${props.dimensions?.width ? `width: ${props.theme.pxToRem(props.dimensions?.width)}` : ''};
          ${props.dimensions?.height ? `height: ${props.theme.pxToRem(props.dimensions?.height)}` : ''};
          align-content: center;
          margin: 0 auto -1rem auto;
        `
      default:
        return css`
          ${props.dimensions?.width ? `width: ${props.theme.pxToRem(props.dimensions?.width)}` : ''};
          ${props.dimensions?.height ? `height: ${props.theme.pxToRem(props.dimensions?.height)}` : ''};
          max-width: 100%;
          max-height: 100%;
          margin: ${spacing.empty} ${props.isArcadeMigrationEnabled ? 0 : spacing.xs}; /* mirror inset found on TextElement */
        `
    }
  }}
`

const RemoteImageElement = ({
  image,
  alignment,
  width,
  height,
  accessibility_label,
}: FormBlockerElementRemoteImageElement) => {
  const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('apps')
  const { darkMode } = useTheme()

  const lightUrl = image?.light_url
  const darkUrl = image?.dark_url ?? lightUrl

  const url = replaceWithCDNImageUrl({ url: darkMode ? darkUrl : lightUrl })

  const name = url
    ?.replace(/.+\//, '')
    ?.replace(/(dark|light)-/, '')
    ?.replace(/_(dark|light)/, '')
    ?.replace(/violet-/, '')
    ?.replace(/\..+$/, '')
    ?.replace(/\?.*$/, '')

  return (
    <Alignment alignment={alignment}>
      <RemoteImageContainer
        name={name}
        dimensions={{ width, height }}
        isArcadeMigrationEnabled={isArcadeMigrationEnabled}
      >
        <img alt={accessibility_label} src={url} height="auto" width="100%" />
      </RemoteImageContainer>
    </Alignment>
  )
}

export default RemoteImageElement
